import React from 'react';
import { Player } from '../../types/player';
import { useQueueStore } from '../../stores/queueStore';

interface QueueDisplayProps {
  className?: string;
}

const QueueDisplay: React.FC<QueueDisplayProps> = ({ className = '' }) => {
  const { queue, currentPlayers, restingPlayers, isLoading } = useQueueStore();

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!queue) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="text-center text-gray-500">
          <p className="text-lg mb-2">队列未初始化</p>
          <p className="text-sm">请先添加玩家并初始化队列</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-6">当前队列</h2>
      
      {/* 参赛者区域 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-green-700 mb-4 flex items-center">
          <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
          参赛者 ({currentPlayers.length}/4)
        </h3>
        
        {currentPlayers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {currentPlayers.map((player, index) => (
              <PlayerCard
                key={player.id}
                player={player}
                position={index + 1}
                type="playing"
              />
            ))}
          </div>
        ) : (
          <div className="text-gray-500 text-center py-4">
            没有参赛者
          </div>
        )}
      </div>

      {/* 休息者区域 */}
      {restingPlayers.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-orange-700 mb-4 flex items-center">
            <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
            休息中 ({restingPlayers.length})
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {restingPlayers.map((player, index) => (
              <PlayerCard
                key={player.id}
                player={player}
                position={currentPlayers.length + index + 1}
                type="resting"
              />
            ))}
          </div>
        </div>
      )}

      {/* 队列统计信息 */}
      <div className="bg-gray-50 rounded-lg p-4 mt-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {queue.players.length}
            </div>
            <div className="text-sm text-gray-600">总人数</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {currentPlayers.length}
            </div>
            <div className="text-sm text-gray-600">参赛中</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">
              {restingPlayers.length}
            </div>
            <div className="text-sm text-gray-600">休息中</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-purple-600">
              {queue.currentPosition}
            </div>
            <div className="text-sm text-gray-600">当前轮次</div>
          </div>
        </div>
      </div>

      {/* 更新时间 */}
      <div className="text-xs text-gray-500 text-center mt-4">
        最后更新: {new Date(queue.updatedAt).toLocaleString('zh-CN')}
      </div>
    </div>
  );
};

interface PlayerCardProps {
  player: Player;
  position: number;
  type: 'playing' | 'resting';
}

const PlayerCard: React.FC<PlayerCardProps> = ({ player, position, type }) => {
  const bgColor = type === 'playing' 
    ? 'bg-green-50 border-green-200' 
    : 'bg-orange-50 border-orange-200';
  
  const textColor = type === 'playing' 
    ? 'text-green-800' 
    : 'text-orange-800';

  const numberBg = type === 'playing'
    ? 'bg-green-500'
    : 'bg-orange-500';

  return (
    <div className={`${bgColor} border-2 rounded-lg p-3 flex items-center space-x-3`}>
      {/* 位置编号 */}
      <div className={`${numberBg} text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold`}>
        {position}
      </div>
      
      {/* 玩家信息 */}
      <div className="flex-1">
        <div className={`font-semibold ${textColor}`}>
          {player.name}
        </div>
        <div className="text-xs text-gray-600">
          编号: {player.number}
        </div>
      </div>

      {/* 状态指示器 */}
      <div className={`w-2 h-2 rounded-full ${type === 'playing' ? 'bg-green-500' : 'bg-orange-500'}`}></div>
    </div>
  );
};

export default QueueDisplay;